// ═══════════════════════════════════════════════════════════════
// TenantLifecycleGate.jsx — Unified gate for cancelled/archived states
// Wraps app content: shows block page for archived, read-only banner
// for cancelled (org or personal). Support + grandfathered bypass.
// ═══════════════════════════════════════════════════════════════

import React, { useCallback } from 'react';
import { Archive, AlertCircle } from 'lucide-react';
import { useBilling } from './BillingProvider.jsx';
import { DEFAULT_CONFIG } from './BillingConfig.js';
import { canAccessNonParticipantRole } from './BillingEnforcement.js';
import { useIsTwaSubscriptionGated } from '../../TwaContext.jsx';

/**
 * <TenantLifecycleGate>
 *   {children}
 * </TenantLifecycleGate>
 *
 * Must be inside <BillingProvider>.
 * - Archived org tenant → full block page (export contact info only)
 * - Cancelled org tenant → read-only banner + children (canEdit=false handled by components)
 * - Cancelled personal plan → read-only banner + children
 * - Support role / grandfathered → bypass entirely
 */
function TenantLifecycleGate({ children, onLogout, allowAccountManagement = false }) {
  const { tenant, billing, billingReadOnly, daysRemaining, stripeAdapter, user, isGrandfathered } = useBilling();
  const twaSubGated = useIsTwaSubscriptionGated(user);

  // Support bypasses all gates
  if (user?.role === 'support') return children;

  const requiresMembership = ['mentor', 'fly-on-wall', 'fly-on-the-wall', 'omniscient'].includes(user?.role);
  if (requiresMembership && !allowAccountManagement && !canAccessNonParticipantRole(billing, tenant).allowed) {
    return (
      <main style={{ maxWidth: 640, margin: '48px auto', padding: 24 }}>
        <h1>Membership required</h1>
        <p>An active Personal+ or organizational plan is required to view connected conflicts, comment, or use mentor messages.</p>
        <p>Your connections are saved. Choose an eligible plan to restore access.</p>
        {!twaSubGated && <p><a href="/?choosePlan=1">Choose an eligible plan</a></p>}
        <a href="/free">Open Communication Tools</a>
        <button type="button" onClick={onLogout} style={{ display: 'block', marginTop: 24 }}>Log out</button>
      </main>
    );
  }

  // Grandfathered users bypass all gates
  if (isGrandfathered) return children;

  // Archived org tenant — full block
  if (tenant && tenant.id !== 'tenant-public' && tenant.status === 'archived') {
    return <ArchivedBlock tenantName={tenant.name} />;
  }

  // Cancelled org tenant — read-only with banner
  if (tenant && tenant.id !== 'tenant-public' && tenant.status === 'cancelled') {
    return (
      <>
        <CancelledOrgBanner daysRemaining={daysRemaining} stripeAdapter={stripeAdapter} user={user} />
        {children}
      </>
    );
  }

  // Cancelled personal plan — read-only with banner
  if (billing?.planStatus === 'cancelled') {
    return (
      <>
        <CancelledPersonalBanner stripeAdapter={stripeAdapter} user={user} />
        {children}
      </>
    );
  }

  return children;
}

/**
 * Full block page for archived tenants.
 */
function ArchivedBlock({ tenantName }) {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-stone-100 flex items-center justify-center p-4">
      <div className="max-w-md w-full bg-white rounded-2xl shadow-lg p-8 text-center">
        <Archive className="w-12 h-12 text-stone-500 mx-auto mb-4" />
        <h2 className="text-xl font-semibold text-stone-800 mb-2">Account Archived</h2>
        <p className="text-sm text-stone-600 mb-4">
          {tenantName ? `"${tenantName}"` : 'This organization'} has been archived.
          Your data is securely retained per our data retention policy.
        </p>
        <p className="text-xs text-stone-500">
          To request data exports or reactivation, contact{' '}
          <a href="mailto:support@theargumentor.com" className="text-amber-700 hover:underline">
            support@theargumentor.com
          </a>
        </p>
      </div>
    </div>
  );
}

/**
 * Banner for cancelled org tenants (read-only grace period).
 */
function CancelledOrgBanner({ daysRemaining, stripeAdapter, user }) {
  const handleResubscribe = useCallback(async () => {
    try {
      const data = await stripeAdapter.createPortalSession({
        userId: user?.id,
        returnUrl: window.location.href,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      console.error('Portal error:', e);
    }
  }, [stripeAdapter, user?.id]);

  const twaSubGated = useIsTwaSubscriptionGated(user);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
      <div className="bg-red-50 border border-red-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-red-700 flex-shrink-0" />
        <p className="text-sm text-red-800 flex-1">
          Your organization's subscription has been cancelled.
          {daysRemaining != null && ` You have ${daysRemaining} day${daysRemaining !== 1 ? 's' : ''} remaining before archival.`}
          {' '}All conflicts are read-only. You can still export your data.
          {twaSubGated && ' Sign in at theargumentor.com from a web browser to resubscribe.'}
        </p>
        {!twaSubGated && (
          <button
            onClick={handleResubscribe}
            className="px-3 py-1.5 bg-red-100 text-red-700 hover:bg-red-200 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
          >
            Resubscribe
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Banner for cancelled personal plans (read-only, resubscribe).
 */
function CancelledPersonalBanner({ stripeAdapter, user }) {
  const handleResubscribe = useCallback(async () => {
    try {
      const data = await stripeAdapter.createPortalSession({
        userId: user?.id,
        returnUrl: window.location.href,
      });
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (e) {
      console.error('Portal error:', e);
    }
  }, [stripeAdapter, user?.id]);

  const twaSubGated = useIsTwaSubscriptionGated(user);

  return (
    <div className="max-w-7xl mx-auto px-3 sm:px-6 pt-4">
      <div className="bg-stone-50 border border-stone-200 rounded-xl px-4 py-3 flex items-center gap-3">
        <AlertCircle className="w-5 h-5 text-stone-500 flex-shrink-0" />
        <p className="text-sm text-stone-700 flex-1">
          Your subscription has ended.
          {twaSubGated
            ? ' Sign in at theargumentor.com from a web browser to resubscribe.'
            : ' Resubscribe to continue creating and editing conflicts.'}
          {' '}You can still view and export your data.
        </p>
        {!twaSubGated && (
          <button
            onClick={handleResubscribe}
            className="px-3 py-1.5 bg-stone-200 text-stone-700 hover:bg-stone-300 rounded-lg text-xs font-medium transition-colors whitespace-nowrap"
          >
            Resubscribe
          </button>
        )}
      </div>
    </div>
  );
}

export default TenantLifecycleGate;
